/**
 * Where a previewed file CAME FROM — and what the one control that acts on it does (owner directive 2026-08-24,
 * round 2; lifted out of src/components/FilePreview/previewOrigin.tsx unchanged, PV5-8:C part 1, 2026-10-13).
 *
 * A preview is a window onto a file, but a file on this platform always has a
 * story: the conversation it was produced in or dropped into. This module
 * carries that origin through every preview shell and holds what the single
 * control that reunites the two does — "open chat", which brings Prest0n up as the
 * docked right rail while the preview keeps the rest of the screen
 * (FilePreviewOverlay's companion layout does the reflow; App's
 * RoutedContentShell moves the page under both). Every shell draws the control from
 * this answer, so the dispatch can never drift between them.
 *
 * Origin is best-effort by design. The chat's own attachment cards know the
 * thread for free (the transcript on screen IS the active thread); a file
 * opened from a bare link knows nothing. Both are useful: with a thread the
 * chat returns to that exact conversation, without one it simply comes up
 * beside the preview on whatever conversation is already open.
 */
import type React from 'react';
import { requestChatOpenForThread } from '../../components/GlobalChatSystem/openChatForFile';
import type { AttachmentResolveContext } from '../../components/FilePreview/resolveAttachment';

/**
 * Extends the server-resolver context (messageId/sessionId/turnId — used to
 * recover a locator-free attachment's bytes) with the CONVERSATION identity
 * the companion control needs. One object travels with a preview so a shell
 * never has to choose which of two context bags to forward.
 */
export interface PreviewOriginContext extends AttachmentResolveContext {
  /** Thread the file was previewed from, when the opener knew it. */
  threadId?: string;
  /** That thread's title, for switchThread's minimal-record path. */
  threadTitle?: string;
}

/**
 * Ask the chat to come up beside this preview, on the file's own
 * conversation when we know it. Returns true when a visible chat took it.
 */
export function openChatBesideFile(origin?: PreviewOriginContext | null): boolean {
  return requestChatOpenForThread({
    threadId: origin?.threadId,
    fallbackTitle: origin?.threadTitle,
    // The whole point of the control: the pane docks right and this preview
    // keeps the left. A floating/centered pane would cover the file.
    forceDocked: true,
  });
}

export interface ChatBesideControl {
  /** The file's own conversation is known: the control returns to it (its words say so). */
  hasThread: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}

/** What the "open chat" control does, for any shell to draw. */
export function chatBesideControl(origin?: PreviewOriginContext | null, onOpened?: () => void): ChatBesideControl {
  return {
    hasThread: Boolean(origin?.threadId),
    // The floating window drags by its header: a pointer-down on a header
    // button must not also start a window drag (sibling tools do the same).
    onPointerDown: (e) => e.stopPropagation(),
    onClick: (e) => {
      e.stopPropagation();
      if (openChatBesideFile(origin)) onOpened?.();
    },
  };
}
